// Stripe payment handler - combines webhook events and client-side utilities
import { doc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { loadStripe } from '@stripe/stripe-js';

// Load Stripe with your TEST key
const stripePromise = loadStripe('pk_test_51QspZNRqVHpWN5FD5wMULUY39fxjlZKi7vMhsOZlojEZi0UfvpDCot10EknJKfqnJf0zQSGcsunJWkmRT5Cx2RDV00coaxX621');

// Webhook handler for Stripe events (server-side)
export const handleStripeWebhook = async (event, signingSecret) => {
  // This would be called from your Firebase function
  // The actual signature verification happens on the server side
  
  try {
    switch (event.type) {
      case 'payment_intent.succeeded':
        await handlePaymentSuccess(event.data.object);
        break;
        
      case 'payment_intent.payment_failed':
        await handlePaymentFailure(event.data.object);
        break;
        
      case 'checkout.session.completed':
        await handleCheckoutCompleted(event.data.object);
        break;
        
      case 'payment_intent.canceled':
        await handlePaymentCanceled(event.data.object);
        break;
        
      case 'checkout.session.expired':
        await handleCheckoutExpired(event.data.object);
        break;
        
      case 'charge.succeeded':
        await handleChargeSuccess(event.data.object);
        break;
        
      case 'charge.failed':
        await handleChargeFailure(event.data.object);
        break;
        
      default:
        console.log(`Unhandled event type: ${event.type}`);
    }
    
    return { received: true };
    
  } catch (err) {
    console.error('Webhook handling failed:', err.message);
    return { error: err.message };
  }
};

// Client-side payment utilities
export const createPaymentIntent = async (orderData) => {
  console.log('🔧 StripeWebhookHandler.createPaymentIntent called with:', orderData);
  
  try {
    // Call Firebase function to create payment intent
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../firebase');
    
    const createPaymentIntentFunction = httpsCallable(functions, 'createPaymentIntent');
    
    const paymentIntentData = {
      amount: Math.round(orderData.total * 100), // Convert to cents
      currency: 'usd',
      metadata: {
        orderId: orderData.orderId || 'temp_' + Date.now(),
        customerEmail: orderData.customer.email,
        customerName: orderData.customer.name,
        shippingType: orderData.shipping?.type || 'delivery',
        subtotal: orderData.subtotal,
        tax: orderData.tax,
        shipping: orderData.shipping.cost
      },
      automatic_payment_methods: {
        enabled: true
      },
      capture_method: 'automatic_async', // Use async capture for faster processing
      confirmation_method: 'automatic'
    };

    console.log('📋 Calling Firebase createPaymentIntent with:', paymentIntentData);
    const result = await createPaymentIntentFunction(paymentIntentData);
    const { client_secret } = result.data;
    
    console.log('✅ Payment intent created successfully:', client_secret);
    return client_secret;
    
  } catch (error) {
    console.error('❌ Error creating payment intent:', error);
    throw error;
  }
};

export const confirmPayment = async (clientSecret, paymentMethodId) => {
  console.log('🔧 StripeWebhookHandler.confirmPayment called');
  console.log('🔑 Client secret:', clientSecret);
  console.log('💳 Payment method ID:', paymentMethodId);
  
  try {
    const stripe = await stripePromise;
    
    // Real Stripe payment confirmation with auto-capture
    console.log('🔐 Using real Stripe payment confirmation with auto-capture');
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethodId,
      setup_future_usage: 'off_session', // Allow future payments
      return_url: `${window.location.origin}/payment-success` // Return URL after payment
    });

    if (error) {
      console.error('❌ Stripe payment confirmation error:', error);
      throw error;
    }

    console.log('✅ Payment confirmed with auto-capture:', paymentIntent);
    return paymentIntent;
    
  } catch (error) {
    console.error('❌ Error confirming payment:', error);
    throw error;
  }
};

export const createPaymentMethod = async (cardElement, billingDetails) => {
  try {
    const stripe = await stripePromise;
    
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: billingDetails,
    });

    if (error) {
      throw error;
    }

    return paymentMethod;
  } catch (error) {
    console.error('Error creating payment method:', error);
    throw error;
  }
};

export const handlePaymentSuccess = async (paymentIntent) => {
  // Update order status to 'paid' in Firestore
  console.log('Payment succeeded:', paymentIntent.id);
  
  const orderId = paymentIntent.metadata.orderId;
  if (orderId) {
    await updateDoc(doc(db, 'orders', orderId), {
      status: 'paid',
      paymentStatus: 'succeeded',
      paymentId: paymentIntent.id,
      updatedAt: serverTimestamp()
    });

    // Create payment record
    await addDoc(collection(db, 'payments'), {
      paymentIntentId: paymentIntent.id,
      orderId: orderId,
      status: 'succeeded',
      amount: paymentIntent.amount / 100,
      currency: paymentIntent.currency,
      createdAt: serverTimestamp()
    });
  }
};

export const handlePaymentFailure = async (paymentIntent) => {
  // Update order status to 'failed'
  console.log('Payment failed:', paymentIntent.id);
  
  const orderId = paymentIntent.metadata.orderId;
  if (orderId) {
    await updateDoc(doc(db, 'orders', orderId), {
      status: 'payment_failed',
      paymentStatus: 'failed',
      paymentId: paymentIntent.id,
      error: paymentIntent.last_payment_error?.message,
      updatedAt: serverTimestamp()
    });

    // Create payment record
    await addDoc(collection(db, 'payments'), {
      paymentIntentId: paymentIntent.id,
      orderId: orderId,
      status: 'failed',
      amount: paymentIntent.amount / 100,
      currency: paymentIntent.currency,
      error: paymentIntent.last_payment_error?.message,
      createdAt: serverTimestamp()
    });
  }
};

export const handleCheckoutCompleted = async (session) => {
  // Handle successful checkout
  console.log('Checkout completed:', session.id);
  
  // Create order from session data if needed
  const orderId = session.metadata.orderId;
  if (orderId) {
    await updateDoc(doc(db, 'orders', orderId), {
      status: 'confirmed',
      paymentStatus: 'succeeded',
      checkoutSessionId: session.id,
      updatedAt: serverTimestamp()
    });
  }
};

export const handlePaymentCanceled = async (paymentIntent) => {
  // Handle canceled payment
  console.log('Payment canceled:', paymentIntent.id);
  
  const orderId = paymentIntent.metadata.orderId;
  if (orderId) {
    await updateDoc(doc(db, 'orders', orderId), {
      status: 'canceled',
      paymentStatus: 'canceled',
      paymentId: paymentIntent.id,
      updatedAt: serverTimestamp()
    });
  }
};

export const handleCheckoutExpired = async (session) => {
  // Handle expired checkout
  console.log('Checkout expired:', session.id);
  
  const orderId = session.metadata.orderId;
  if (orderId) {
    await updateDoc(doc(db, 'orders', orderId), {
      status: 'expired',
      paymentStatus: 'expired',
      checkoutSessionId: session.id,
      updatedAt: serverTimestamp()
    });
  }
};

export const handleChargeSuccess = async (charge) => {
  // Handle successful charge
  console.log('Charge succeeded:', charge.id);
  
  // Additional charge handling if needed
};

export const handleChargeFailure = async (charge) => {
  // Handle failed charge
  console.log('Charge failed:', charge.id);
  
  // Additional charge failure handling if needed
};

// Get stripe instance
export const getStripe = () => stripePromise;
